import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, CheckCircle, Zap, Eye, Star, PoundSterling, Target, Clock, Award, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import PageHeader from '../components/PageHeader';
import PageFooter from '../components/PageFooter';
import HeadlineAccent from '../components/HeadlineAccent';

const fadeUp = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-40px' },
    transition: { duration: 0.5 }
};

const stagger = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
};

const CORE_CARDS = [
    {
        icon: Eye,
        title: 'No CV guesswork',
        desc: 'Forget the paper shuffle. You are evaluated on how you actually perform — not how well you format a document.',
        span: 'md:col-span-4 md:row-span-2',
    },
    {
        icon: Zap,
        title: 'Show your ability',
        desc: 'Our assessments reveal your real strengths — problem solving, communication, execution — so employers see who you really are.',
        span: 'md:col-span-8',
    },
    {
        icon: Star,
        title: 'Talent Card + Wildcard',
        desc: 'Your AI-generated Talent Card highlights your standout attributes. Exceptional performers may earn "Wildcard" status — a rare observational designation.',
        span: 'md:col-span-4',
    },
    {
        icon: PoundSterling,
        title: 'Paid trials',
        desc: 'Every trial shift is paid. Prove what you can do on the job and get hired based on real output — not promises.',
        span: 'md:col-span-4',
    },
];

const STEPS = [
    { num: '01', title: 'Build your Talent Card', desc: 'Answer questions about your experience, skills, and work style to generate your AI-powered profile.' },
    { num: '02', title: 'Complete your assessment', desc: 'Take the integrated Sampler assessment — a single guided wizard that captures your skills, work style, and behavioural strengths in one go.' },
    { num: '03', title: 'Get discovered', desc: 'Employers find you based on your performance scores, not keyword-stuffed CVs.' },
    { num: '04', title: 'Paid trial shift', desc: 'Get invited to a paid trial (1-10 days) where you demonstrate your skills in a real work environment.' },
    { num: '05', title: 'Get hired on output', desc: 'Your trial performance speaks for itself. No more guessing — employers hire based on evidence.' },
];

const BENEFITS = [
    { icon: Target, title: 'Fairer opportunities', desc: 'Your performance matters more than your background or connections.' },
    { icon: Clock, title: 'Faster hiring', desc: 'Skip months of applications. Get matched, trialled, and hired in days.' },
    { icon: PoundSterling, title: 'Paid trials', desc: 'Every trial is paid at fair rates. Your time is always valued.' },
    { icon: Award, title: 'Performance-based', desc: 'Build a track record that follows you. Great work compounds.' },
];

const AUDIENCE = [
    'People who are consistently overlooked by CV-based screening',
    'Strong performers who don\'t shine in traditional interviews',
    'Anyone confident enough to let their work speak for itself',
    'Career changers ready to break into a new industry and back themselves to deliver',
    'Anyone early in their career who knows their potential outweighs their CV',
];

const FAQ_ITEMS = [
    { q: 'How does the Talent Card work?', a: 'Your Talent Card is generated using AI after you complete our assessments. It summarises your key strengths, work style, and performance scores in a format employers can quickly evaluate.' },
    { q: 'What is a Wildcard?', a: 'A rare platform designation for candidates whose profile suggests potential that sits outside the obvious. You cannot apply for it and you cannot optimise for it — it is observational.' },
    { q: 'Are trial shifts really paid?', a: 'Yes — every single trial shift on Sampler is paid. Employers pay a flat daily rate that covers your compensation. You will never work for free.' },
    { q: 'How long are trial shifts?', a: 'Trials can be anywhere from 1 to 10 days, running from 10am to 5pm. The duration is agreed between you and the employer before the trial begins.' },
    { q: 'What sectors does Sampler cover?', a: 'We currently cover 9 office-based sectors: Property, Tech & Product, Sales & Account Management, Creative & Events, HR & People, Marketing & Growth, Operations & Admin, Finance & Accounting, and Legal & Compliance — with 112 roles across them.' },
];

const ForTalent = () => {
    const navigate = useNavigate();
    const prefersReducedMotion = useReducedMotion();

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#F0FDF4' }}>
            <Helmet>
                <title>Sampler | For Talent</title>
                <meta name="description" content="Complete a real assessment, run a paid trial shift, and get hired based on performance — not your CV." />
            </Helmet>
            <PageHeader />

            {/* Hero */}
            <section className="py-24 md:py-32 overflow-hidden" data-testid="talent-hero">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                        <motion.div className="md:col-span-7" {...fadeUp}>
                            <span
                                className="inline-block uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] mb-6"
                                style={{ fontFamily: 'Unbounded, sans-serif' }}
                                data-testid="talent-hero-label"
                            >
                                For Talent
                            </span>
                            <h1
                                className="text-5xl sm:text-6xl font-bold tracking-tight leading-none mb-6"
                                style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}
                                data-testid="talent-hero-heading"
                            >
                                <HeadlineAccent>Prove</HeadlineAccent>{' '}
                                what you can do and get hired for it.
                            </h1>
                            <p
                                className="text-base md:text-lg leading-relaxed text-slate-700 mb-8 max-w-xl"
                                style={{ fontFamily: 'Barlow, sans-serif' }}
                                data-testid="talent-hero-sub"
                            >
                                Show what you&rsquo;re made of and let your work do the talking
                            </p>
                            <Button
                                className="btn-primary text-lg h-14 px-10"
                                onClick={() => navigate('/register?type=candidate')}
                                data-testid="talent-hero-cta"
                            >
                                Join the Founding 1,000
                                <ArrowRight className="ml-2 h-5 w-5" />
                            </Button>
                        </motion.div>
                        <motion.div
                            className="md:col-span-5"
                            initial={{ opacity: 0, x: 40 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                        >
                            <motion.img
                                src="https://static.prod-images.emergentagent.com/jobs/a8879e52-a752-49c0-9edd-013047c3b666/images/fe6f7d6ca209774f21723eb36dc226dd6c5c5b6309e52e615a1b59983411e77d.png"
                                alt="Talent Cards"
                                className="w-full max-w-md mx-auto drop-shadow-2xl"
                                animate={prefersReducedMotion ? undefined : { y: [0, -10, 0] }}
                                transition={prefersReducedMotion ? undefined : { duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                                data-testid="talent-hero-image"
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Core Difference — Bento Grid */}
            <section className="py-24 md:py-32" data-testid="talent-core-difference">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            The Core Difference
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Why Sampler changes everything
                        </h2>
                    </motion.div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 auto-rows-auto">
                        {CORE_CARDS.map((card, i) => {
                            return (
                                <motion.div
                                    key={i}
                                    className={`bg-[#F3E8FF] rounded-3xl p-8 border border-purple-200/50 shadow-sm hover:shadow-md hover:border-purple-300 transition-all duration-300 ${card.span}`}
                                    {...stagger}
                                    transition={{ duration: 0.45, delay: i * 0.1 }}
                                    data-testid={`talent-core-card-${i}`}
                                >
                                    <h3 className="mb-3" style={{ fontFamily: 'Unbounded, sans-serif', fontSize: '20px', fontWeight: 700, color: '#0A0A0A' }}>
                                        {card.title}
                                    </h3>
                                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                        {card.desc}
                                    </p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-24 md:py-32" style={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }} data-testid="talent-how-it-works">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            How It Works
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Five steps to getting hired
                        </h2>
                        <p className="text-slate-500 mt-3 text-base" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            No endless applications. No guesswork.
                        </p>
                    </motion.div>
                    <div className="space-y-0">
                        {STEPS.map((step, i) => (
                            <motion.div
                                key={i}
                                className="flex items-start gap-6 md:gap-10 py-8 border-b border-purple-200/40 last:border-b-0"
                                {...stagger}
                                transition={{ duration: 0.4, delay: i * 0.08 }}
                                data-testid={`talent-step-${i}`}
                            >
                                <span
                                    className="text-3xl md:text-4xl font-bold text-[#8B5CF6]/30 shrink-0 w-16 text-right"
                                    style={{ fontFamily: 'Unbounded, sans-serif' }}
                                >
                                    {step.num}
                                </span>
                                <div>
                                    <h3 className="text-lg md:text-xl font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                        {step.title}
                                    </h3>
                                    <p className="text-slate-600 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                        {step.desc}
                                    </p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Who This Is For */}
            <section className="py-24 md:py-32" data-testid="talent-who-for">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="max-w-3xl">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Who This Is For
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-10" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Built for people who know they can deliver
                        </h2>
                        <div className="space-y-5">
                            {AUDIENCE.map((item, i) => (
                                <motion.div
                                    key={i}
                                    className="flex items-start gap-4"
                                    {...stagger}
                                    transition={{ duration: 0.4, delay: i * 0.1 }}
                                    data-testid={`talent-audience-${i}`}
                                >
                                    <CheckCircle className="h-6 w-6 text-[#10B981] shrink-0 mt-0.5" strokeWidth={2.5} />
                                    <p className="text-lg text-slate-700" style={{ fontFamily: 'Barlow, sans-serif' }}>{item}</p>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-24 md:py-32" style={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }} data-testid="talent-benefits">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-16">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Benefits
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            What you get with Sampler
                        </h2>
                    </motion.div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
                        {BENEFITS.map((b, i) => {
                            const Icon = b.icon;
                            return (
                                <motion.div
                                    key={i}
                                    className="bg-white rounded-2xl p-6 border border-purple-200/50 shadow-sm hover:shadow-md transition-all duration-300"
                                    {...stagger}
                                    transition={{ duration: 0.4, delay: i * 0.08 }}
                                    data-testid={`talent-benefit-${i}`}
                                >
                                    <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: '#10B981' }}>
                                        <Icon className="h-5 w-5 text-white" strokeWidth={2.5} />
                                    </div>
                                    <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>{b.title}</h3>
                                    <p className="text-sm text-slate-600" style={{ fontFamily: 'Barlow, sans-serif' }}>{b.desc}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Social Proof */}
            <section className="py-24 md:py-32" data-testid="talent-social-proof">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="max-w-3xl mx-auto text-center">
                        <div className="bg-white rounded-3xl p-10 md:p-14 border border-purple-200/50 shadow-sm" data-testid="talent-testimonial-card">
                            <p className="text-xl md:text-2xl font-medium leading-relaxed text-center" style={{ fontFamily: 'Barlow, sans-serif', color: '#0A0A0A' }}>
                                &ldquo;We don&rsquo;t have testimonials yet. We&rsquo;re new. But we know exactly why you&rsquo;re here.&rdquo;
                            </p>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Founding 1000 */}
            <section className="py-24 md:py-32" data-testid="talent-founding-1000">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <motion.div
                        {...fadeUp}
                        className="rounded-3xl p-10 md:p-16 text-center"
                        style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4C1D95 50%, #7C3AED 100%)' }}
                    >
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#A78BFA] block mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Early Access
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            Join the Founding 1,000
                        </h2>
                        <p className="text-purple-200 mb-8 max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Be among the first talent on the platform. Founding members receive a permanent badge on their Talent Card.
                        </p>
                        <Button
                            className="rounded-full bg-white text-[#4C1D95] hover:bg-purple-50 font-semibold h-14 px-10 text-lg shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                            onClick={() => navigate('/register?type=candidate')}
                            data-testid="talent-founding-cta"
                        >
                            Claim Your Spot
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </motion.div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32" style={{ backgroundColor: 'rgba(243, 232, 255, 0.3)' }} data-testid="talent-faq">
                <div className="max-w-3xl mx-auto px-6 md:px-12">
                    <motion.div {...fadeUp} className="mb-12">
                        <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                            FAQ
                        </span>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Common questions
                        </h2>
                    </motion.div>
                    <Accordion type="single" collapsible className="w-full">
                        {FAQ_ITEMS.map((faq, i) => (
                            <AccordionItem key={i} value={`faq-${i}`} className="border-purple-200/50" data-testid={`talent-faq-item-${i}`}>
                                <AccordionTrigger className="text-left text-base font-semibold hover:no-underline py-5" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                                    {faq.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-slate-600 leading-relaxed" style={{ fontFamily: 'Barlow, sans-serif' }}>
                                    {faq.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* Final CTA */}
            <section className="py-24 md:py-32" data-testid="talent-final-cta">
                <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
                    <motion.div {...fadeUp}>
                        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>
                            Ready to let your work speak for itself?
                        </h2>
                        <p className="text-lg text-slate-600 mb-8" style={{ fontFamily: 'Barlow, sans-serif' }}>
                            Join the Founding 1,000 and get discovered by employers who care about what you can do.
                        </p>
                        <Button
                            className="btn-primary text-lg h-14 px-10"
                            onClick={() => navigate('/register?type=candidate')}
                            data-testid="talent-final-cta-btn"
                        >
                            Join the Founding 1,000
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                    </motion.div>
                </div>
            </section>

            <PageFooter />
        </div>
    );
};

export default ForTalent;
